<!-- filepath: c:\Users\adilr\Desktop\Megahissotto-F\MegaHissotto-F-master\src\pages\Project.vue -->
<template>
  <main class="bg-gradient-to-b from-blue-900 to-blue-700 min-h-screen text-white py-16 px-4 sm:px-6 lg:px-8">
    <div class="max-w-7xl mx-auto">
      <h1 class="text-4xl md:text-5xl my-12 font-extrabold text-center animate-fade-in">
        {{ t('projets.title') }}
      </h1>
      <div v-if="loading" class="flex justify-center items-center min-h-screen">
        <div class="loader">
          <div class="box box-1">
            <div class="side-left"></div>
            <div class="side-right"></div> 
            <div class="side-top"></div>
          </div>
          <div class="box box-2">
            <div class="side-left"></div>
            <div class="side-right"></div>
            <div class="side-top"></div>
          </div>
          <div class="box box-3">
            <div class="side-left"></div>
            <div class="side-right"></div>
            <div class="side-top"></div>
          </div>
          <div class="box box-4">
            <div class="side-left"></div>
            <div class="side-right"></div>
            <div class="side-top"></div>
          </div>
        </div> 
      </div>
      <div v-else-if="error" class="text-center text-red-500">{{ error.message }}</div>
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div v-for="(project, index) in projects" :key="project.id" 
             class="project-card bg-white/10 rounded-lg overflow-hidden shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-2xl hover:scale-105"
             :style="{ animationDelay: `${index * 150}ms` }">
          <div class="relative overflow-hidden group">
            <img :src="`${API_BASE_URL}/storage/${project.img.replace('public/', '')}`"  
            :alt="project.title" 
            class="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110">
            <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-start p-4">
              <span class="text-lg font-semibold">{{ project.title }}</span>
            </div>
          </div>
          <div class="p-4">
            <p class="text-sm mb-4">{{ project.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDataStore } from '@/stores/mainStore'
import { API_BASE_URL } from '@/apiConfig';
import { useHead } from '@vueuse/head';

useHead({
  title: 'Nos Réalisations | Mega Hissotto – Projets Climatisation & Froid Industriel Algérie',
  meta: [
    { name: 'description', content: 'Retrouvez les réalisations et projets de Mega Hissotto en matière de climatisation industrielle, froid industriel, pompes à chaleur et free-cooling en Algérie.' },
    { name: 'keywords', content: 'projets climatisation Algérie, réalisations froid industriel, Mega Hissotto projets, HVAC Algérie' },
    { property: 'og:title', content: 'Nos Réalisations | Mega Hissotto' },
    { property: 'og:url', content: 'https://www.mega-hissotto.com/Project' },
  ],
  link: [{ rel: 'canonical', href: 'https://www.mega-hissotto.com/Project' }]
});

const { t } = useI18n()
const dataStore = useDataStore()

const loading = ref(false)
const error = ref(null)
const projects = ref([])

onMounted(async () => {
  loading.value = true
  try {
    await dataStore.fetchData()
    projects.value = dataStore.data.projects
  } catch (err) {
    error.value = err
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.project-card {
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 1s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Loading Spinner */
.loading {
  --speed-of-animation: 0.9s;
  --gap: 6px;
  --first-color: #4c86f9;
  --second-color: #49a84c;
  --third-color: #f6bb02;
  --fourth-color: #f6bb02;
  --fifth-color: #2196f3;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100px;
  gap: 6px;
  height: 100px;
}

.loading span {
  width: 4px;
  height: 50px;
  background: var(--first-color);
  animation: scale var(--speed-of-animation) ease-in-out infinite;
}

.loading span:nth-child(2) {
  background: var(--second-color);
  animation-delay: -0.8s;
}

.loading span:nth-child(3) {
  background: var(--third-color);
  animation-delay: -0.7s;
}

.loading span:nth-child(4) {
  background: var(--fourth-color);
  animation-delay: -0.6s;
}

.loading span:nth-child(5) {
  background: var(--fifth-color);
  animation-delay: -0.5s;
}

@keyframes scale {
  0%, 40%, 100% {
    transform: scaleY(0.05);
  }

  20% {
    transform: scaleY(1);
  }
}

/* Loading Animation */
body.loading::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: loadingFadeOut 0.5s ease-out 1s forwards;
}

body.loading::after {
  content: 'Loading...';
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 50x;
  z-index: 10000;
  animation: loadingFadeOut 0.5s ease-out 1s forwards;
}

@keyframes loadingFadeOut {
  to {
    opacity: 0;
    visibility: hidden;
  }
}
</style>
